// ============================================================
// CENTRALIZED FORMATTING UTILITIES
// Single source of truth for date, token, cost, and duration formatting.
// ============================================================

// ── Global timezone ──────────────────────────────────────────
// Set once on app start from /api/settings/general via TimezoneContext.
// All date formatting functions use this automatically.
let _timezone = undefined // undefined = browser default until loaded

export function setGlobalTimezone(tz) {
  _timezone = tz || undefined
}

export function getGlobalTimezone() {
  return _timezone
}

// ── Date helpers ─────────────────────────────────────────────

/** Parse a date string, appending 'Z' if no timezone indicator present (D1 stores UTC without 'Z'). */
export function parseDate(dateStr) {
  if (!dateStr) return null
  return new Date(typeof dateStr === 'string' && !dateStr.includes('Z') && !dateStr.includes('+')
    ? dateStr + 'Z'
    : dateStr)
}

/**
 * Relative time string ("3m ago", "2h ago", "5d ago").
 * Handles both UTC strings (with/without trailing "Z") and ISO strings.
 */
export function timeAgo(dateStr) {
  const date = parseDate(dateStr)
  if (!date) return ''
  const diffMs = Date.now() - date.getTime()
  if (diffMs < 0) return 'just now'
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

/**
 * Same as timeAgo but returns '-' for null/undefined (used in tables).
 */
export function timeAgoOrDash(dateStr) {
  if (!dateStr) return '-'
  return timeAgo(dateStr)
}

/**
 * Same as timeAgo but returns 'Never' for null/undefined (used in settings).
 */
export function timeAgoOrNever(dateStr) {
  if (!dateStr) return 'Never'
  return timeAgo(dateStr)
}

/**
 * Locale-formatted timestamp: "6 Mar 2026, 18:22"
 * Uses the globally configured timezone.
 */
export function formatTimestamp(dateStr, { seconds = false } = {}) {
  const d = parseDate(dateStr)
  if (!d) return ''
  const opts = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }
  if (seconds) opts.second = '2-digit'
  if (_timezone) opts.timeZone = _timezone
  return d.toLocaleString('en-GB', opts)
}

/**
 * Date-only format: "6 Mar 2026"
 * Uses the globally configured timezone.
 */
export function formatDate(dateStr) {
  const d = parseDate(dateStr)
  if (!d) return ''
  const opts = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }
  if (_timezone) opts.timeZone = _timezone
  return d.toLocaleDateString('en-GB', opts)
}

/**
 * Time-only format: "18:22:05"
 * Uses the globally configured timezone.
 */
export function formatTime(dateStr) {
  const d = parseDate(dateStr)
  if (!d) return ''
  const opts = { hour: '2-digit', minute: '2-digit', second: '2-digit' }
  if (_timezone) opts.timeZone = _timezone
  return d.toLocaleTimeString('en-GB', opts)
}

/**
 * Elapsed duration between two D1 timestamps: "5s", "3m 12s", "1h 14m".
 * If endStr is null, uses current time.  Handles D1's missing-Z timestamps via parseDate().
 */
export function calculateDuration(startStr, endStr) {
  if (!startStr) return 'N/A'
  const start = parseDate(startStr)
  if (!start) return 'N/A'
  const end = endStr ? parseDate(endStr) : new Date()
  const seconds = Math.floor((end - start) / 1000)
  if (seconds < 0) return '0s'
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

/**
 * Duration in human-readable form: "1.2s", "3.5m", "150ms"
 */
export function formatDuration(ms) {
  if (ms == null) return ''
  if (ms >= 60000) return `${(ms / 60000).toFixed(1)}m`
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.round(ms)}ms`
}

/**
 * Token count: "1.2M", "45.3K", "500"
 */
export function formatTokens(val) {
  if (val == null || val === 0) return '0'
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`
  if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`
  return String(val)
}

/**
 * USD cost: "$0.0042"
 */
export function formatCost(val) {
  if (val == null) return 'N/A'
  return `$${Number(val).toFixed(4)}`
}

/**
 * AI stats summary line: "gpt-4 · 1,234 tokens · 2.1s · $0.0042 · 3 tool calls"
 */
export function formatAIStats(stats) {
  if (!stats) return ''
  const parts = []
  if (stats.model) parts.push(stats.model)
  if (stats.total_tokens) parts.push(`${stats.total_tokens.toLocaleString()} tokens`)
  if (stats.duration_ms) parts.push(formatDuration(stats.duration_ms))
  const cost = typeof stats.cost_usd === 'number' ? stats.cost_usd : stats.cost_usd?.total_cost_usd
  if (cost) parts.push(`$${cost.toFixed(4)}`)
  if (stats.tool_calls?.length) parts.push(`${stats.tool_calls.length} tool call${stats.tool_calls.length > 1 ? 's' : ''}`)
  return parts.join(' \u00B7 ')
}

